import React, { ReactElement, useContext } from 'react'
// Externals imports.
import {
    DateTimePicker as MUIDateTimePicker,
    DatePicker as MUIDatePicker,
    TimePicker as MUITimePicker,
    MuiPickersUtilsProvider
} from 'akixi-material-ui-pickers'
import { MaterialUiPickersDate } from 'akixi-material-ui-pickers/typings/date'
import MomentUtils from '@date-io/moment'
import { DateTimePickerType, DateTimePickerVariant } from './Constants'
import Label from '../Label'
import UserInfoContext, { UserInfoContextType } from '../../Contexts/UserInfoContext'
// CSS imports. Commented out because of current build process.
// import './dateTimePicker.scss'

/**
 * Common type for properties used in all types of MUI pickers.
 */
type CommonPickerProps = {
    variant: 'dialog' | 'inline' | 'static'
    value: Date | null
    onChange: (dtDate: MaterialUiPickersDate | null) => void
    orientation: 'portrait' | 'landscape'
    format: string
    ampm?: boolean
}

/**
 * Component properties type.
 */
type Props = {
    type: DateTimePickerType
    variant: DateTimePickerVariant
    value: Date | null
    handleChange: (dtNewDate: Date) => void
    format: string
    labelLeft?: string
    timeViews?: ('hours' | 'minutes' | 'seconds')[]
    dateTimeViews?: ('year' | 'month' | 'date' | 'hours' | 'minutes' | 'seconds')[]
    timeMinutesStep?: number
    isAMPMDisplayed?: boolean
}

/**
 * Date time picker component.
 * @param type The type of the picker. It could be the picker for date or time or both.
 * @param variant Display variant.
 * @param value Current value.
 * @param handleChange Change handler.
 * @param format Format string for the value displayed.
 * @param labelLeft Label on left side of the input.
 * @param timeViews Views for time picker.
 * @param dateTimeViews Views for date time picker.
 * @param timeMinutesStep Minutes picker step.
 * @param isAMPMDisplayed Whether to display AM/PM picker.
 */
const DateTimePicker: React.FC<Props> = ({
    type,
    variant,
    value,
    handleChange,
    format,
    labelLeft,
    timeViews,
    dateTimeViews,
    timeMinutesStep,
    isAMPMDisplayed
}: Props): ReactElement => {
    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    /**
     * Handles value change.
     * @param dateInternal The internal library date format based on the external library support.
     */
    const handleDateChange = (dateInternal: MaterialUiPickersDate | null): void => {
        if (dateInternal) {
            handleChange(dateInternal.toDate())
        }
    }

    // Common pickers properties.
    const commonProps: CommonPickerProps = {
        variant,
        value,
        onChange: handleDateChange,
        orientation: 'portrait', // Hardcoded as we want to use it consistently.
        format,
        ampm: isAMPMDisplayed
    }

    /**
     * Builds the picker element.
     */
    const getPicker = (): ReactElement => {
        let picker: ReactElement

        switch (type) {
            case DateTimePickerType.DATETIME:
                picker = <MUIDateTimePicker {...commonProps} views={dateTimeViews} />
                break

            case DateTimePickerType.TIME:
                picker = <MUITimePicker {...commonProps} views={timeViews} minutesStep={timeMinutesStep} />
                break

            case DateTimePickerType.DATE:
            default:
                picker = <MUIDatePicker {...commonProps} />
                break
        }

        return picker
    }

    return (
        <MuiPickersUtilsProvider utils={MomentUtils} locale={userInfo?.SessionInfo?.Language}>
            <div className='picker-wrapper'>
                {labelLeft && <Label customClass='label-left'>{labelLeft}</Label>}
                <div className='picker'>{getPicker()}</div>
            </div>
        </MuiPickersUtilsProvider>
    )
}

export default DateTimePicker
